import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCategories,
  toggleCategoryActiveState,
  updateCategory,
  deleteCategory,
} from "@/data/mockData";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Trash,
  Search,
  ArrowUpDown,
  ChevronDown,
  Tag,
  GripVertical,
  Eye,
  Plus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Category } from "@/types/index";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import React from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Get background color based on category name (for avatar)
const getCategoryColor = (categoryName: string): string => {
  // Use the first character of the category name to create a consistent color
  const charCode = categoryName.charCodeAt(0);

  const colorOptions = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
  ];

  return colorOptions[charCode % colorOptions.length];
};

interface SortableCategoryRowProps {
  category: Category;
  onEdit: (id: number) => void;
  onDelete: (id: number, name: string) => void;
  onToggleActive: (id: number, isActive: boolean) => void;
  renderCategoryIcon: (category: Category) => React.ReactNode;
  showDetails: boolean;
  sortBy: "name" | "id" | "booksCount" | "position";
}

function SortableCategoryRow({
  category,
  onEdit,
  onDelete,
  onToggleActive,
  renderCategoryIcon,
  showDetails,
  sortBy,
}: SortableCategoryRowProps) {
  const isDragEnabled = sortBy === "position";
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={`hover:bg-muted/50 ${
        isDragging ? "opacity-50 bg-accent" : ""
      } ${!category.isActive ? "opacity-60" : ""}`}
    >
      {showDetails && (
        <TableCell className="font-mono text-sm">{category.id}</TableCell>
      )}
      <TableCell>
        <div className="flex items-center">
          <div
            {...(isDragEnabled ? attributes : {})}
            {...(isDragEnabled ? listeners : {})}
            className={`${
              isDragEnabled
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-not-allowed opacity-30"
            } p-1 mr-2`}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <Badge variant="outline" className="mr-2">
            {category.position !== undefined && category.position !== null
              ? category.position
              : "—"}
          </Badge>
        </div>
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center">
          {renderCategoryIcon(category)}
          <div className="flex flex-col">
            <span>{category.name}</span>
            {category.name_mz && (
              <span className="text-sm text-muted-foreground font-normal">
                {category.name_mz}
              </span>
            )}
          </div>
          {!category.isActive && (
            <Badge variant="outline" className="ml-2 text-muted-foreground">
              Hidden
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="secondary">{category.booksCount || 0}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-center gap-2">
          <Switch
            checked={category.isActive}
            onCheckedChange={(checked) => onToggleActive(category.id, checked)}
            aria-label={
              category.isActive ? "Category is active" : "Category is hidden"
            }
          />
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ChevronDown className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-popover border-border"
            >
              <DropdownMenuItem
                onClick={() => onEdit(category.id)}
                className="cursor-pointer"
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit category
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <Trash className="mr-2 h-4 w-4" />
                Delete category
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete the category "{category.name}
                  "? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    onDelete(category.id, category.name);
                    setDeleteDialogOpen(false);
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
}

const CategoriesList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<
    "name" | "id" | "booksCount" | "position"
  >("position");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  // Removed analytics panel state for performance
  const [showInactive, setShowInactive] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [localCategories, setLocalCategories] = useState<Category[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [categoryToDelete, setCategoryToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Set up DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only start dragging after moving 8px to prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch categories data
  const { data: fetchedCategories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  // Initialize local categories when data is fetched
  React.useEffect(() => {
    if (fetchedCategories) {
      setLocalCategories(fetchedCategories);
    }
  }, [fetchedCategories]);

  // Use local categories for UI rendering to maintain drag state
  const categories = localCategories.length
    ? localCategories
    : fetchedCategories || [];

  const handleSort = (column: "name" | "id" | "booksCount" | "position") => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    // Skip processing if not sorting by position
    if (sortBy !== "position") return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Find the indexes
      const oldIndex = categories.findIndex(
        (cat) => cat.id === Number(active.id)
      );
      const newIndex = categories.findIndex(
        (cat) => cat.id === Number(over.id)
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create a new array with the reordered items
        const newCategoriesOrder = arrayMove(
          [...categories],
          oldIndex,
          newIndex
        );

        // Update positions based on the new order
        const updatedCategories = newCategoriesOrder.map((cat, index) => ({
          ...cat,
          position: index + 1,
        }));

        // Update local state to immediately reflect the changes
        setLocalCategories(updatedCategories);

        try {
          // Update positions on the server for each category
          for (const cat of updatedCategories) {
            const originalCategory = categories.find((c) => c.id === cat.id);
            if (cat.position !== originalCategory?.position) {
              // Only update if position has changed
              await updateCategory(cat.id, { position: cat.position });
              console.log(
                `Updated category ${cat.id} position to ${cat.position}`
              );
            }
          }

          toast({
            title: "Categories reordered",
            description: "Your changes have been saved.",
          });
        } catch (error) {
          console.error("Error updating category positions:", error);
          toast({
            title: "Error",
            description: "Failed to save the new category order.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await toggleCategoryActiveState(id, isActive);

      // Update local state
      setLocalCategories((prev) =>
        prev.map((cat) => (cat.id === id ? { ...cat, isActive } : cat))
      );

      // In a real app, you would also invalidate the cache to refetch the data
      queryClient.invalidateQueries({ queryKey: ["categories"] });

      toast({
        title: `Category ${isActive ? "activated" : "deactivated"}`,
        description: `Category has been ${
          isActive ? "made public" : "hidden"
        }.`,
      });
    } catch (error) {
      console.error("Error toggling category status:", error);
      toast({
        title: "Error",
        description: "Failed to update category status.",
        variant: "destructive",
      });
    }
  };

  // Handle delete action
  const handleDeleteCategory = async (id: number, name: string) => {
    try {
      // Remove from local state immediately for responsive UI
      setLocalCategories((prev) => prev.filter((cat) => cat.id !== id));

      // Call API to delete
      await deleteCategory(id);

      // Invalidate query to ensure data is fresh
      queryClient.invalidateQueries({ queryKey: ["categories"] });

      toast({
        title: "Category deleted",
        description: `"${name}" has been removed.`,
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: "Error",
        description: "Failed to delete the category.",
        variant: "destructive",
      });

      // Restore local state by fetching fresh data
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Categories">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  // Filter categories based on search query and active state
  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (category.name_mz &&
        category.name_mz.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesActiveState = showInactive || category.isActive;
    return matchesSearch && matchesActiveState;
  });

  // Sort categories
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    if (sortBy === "name") {
      // Primary sort by English name, fallback to Mizo name if available
      const aName = a.name || a.name_mz || "";
      const bName = b.name || b.name_mz || "";
      return sortDirection === "asc"
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    } else if (sortBy === "id") {
      return sortDirection === "asc" ? a.id - b.id : b.id - a.id;
    } else if (sortBy === "booksCount") {
      const countA = a.booksCount || 0;
      const countB = b.booksCount || 0;
      return sortDirection === "asc" ? countA - countB : countB - countA;
    } else if (sortBy === "position") {
      // Define a helper to get the sort value for a position
      // Note that we treat undefined/null as high value, but 0 as a valid position
      const getPositionValue = (pos: number | undefined | null) => {
        return pos === 0 || pos ? pos : 1000;
      };

      const posA = getPositionValue(a.position);
      const posB = getPositionValue(b.position);

      return sortDirection === "asc" ? posA - posB : posB - posA;
    }
    return 0;
  });

  // Removed local analytics computations

  // Handle edit action
  const handleEditCategory = (id: number) => {
    navigate(`/admin/categories/${id}/edit`);
  };

  const addCategoryButton = (
    <Button onClick={() => navigate("/admin/categories/new")}>
      <Plus className="mr-2 h-4 w-4" />
      Add Category
    </Button>
  );

  // Render category icon with fallback for empty/invalid URLs
  const renderCategoryIcon = (category: Category) => {
    const colorClasses = getCategoryColor(category.name);

    return (
      <Avatar className="h-9 w-9 mr-2">
        {category.icon ? (
          <img
            src={category.icon}
            alt={category.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              // If image fails to load, replace with fallback
              e.currentTarget.style.display = "none";
              const fallback = e.currentTarget.parentElement?.querySelector(
                '[data-fallback="true"]'
              ) as HTMLElement | null;
              if (fallback) fallback.style.display = "flex";
            }}
          />
        ) : null}
        <AvatarFallback
          className={colorClasses}
          data-fallback="true"
          style={{ display: category.icon ? "none" : "flex" }}
        >
          {category.name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
    );
  };

  return (
    <AdminLayout title="Categories" actionButton={addCategoryButton}>
      {/* Analytics Overview removed */}

      {/* Search and Filter Card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Search and filter categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search categories (English or Mizo)..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 whitespace-nowrap">
                <Switch
                  id="show-inactive"
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                />
                <Label htmlFor="show-inactive" className="cursor-pointer">
                  Show hidden categories
                </Label>
              </div>

              <div className="flex items-center space-x-2 whitespace-nowrap">
                <Switch
                  id="show-details"
                  checked={showDetails}
                  onCheckedChange={setShowDetails}
                />
                <Label htmlFor="show-details" className="cursor-pointer">
                  Show ID
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drag instruction alert */}
      {sortBy !== "position" && (
        <Alert className="mb-6">
          <Info className="h-4 w-4 mr-2" />
          <AlertDescription>
            Drag and drop reordering is only available when sorting by{" "}
            <strong>Position</strong>. Click on the "Position" column header to
            enable drag and drop functionality.
          </AlertDescription>
        </Alert>
      )}

      {/* Categories Table */}
      <Card>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                {showDetails && (
                  <TableHead
                    className="cursor-pointer w-[100px]"
                    onClick={() => handleSort("id")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>ID</span>
                      {sortBy === "id" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                )}
                <TableHead
                  className="cursor-pointer w-[120px]"
                  onClick={() => handleSort("position")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Position</span>
                    {sortBy === "position" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortBy === "name" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("booksCount")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Books</span>
                    {sortBy === "booksCount" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-center w-[100px]">
                  <div className="flex items-center justify-center">
                    <Eye className="h-4 w-4 mr-1" />
                    <span>Status</span>
                  </div>
                </TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCategories.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={showDetails ? 6 : 5}
                    className="h-24 text-center text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Tag className="h-8 w-8 mb-2 text-muted-foreground/50" />
                      <h3 className="font-medium">No categories found</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery
                          ? `No categories match "${searchQuery}"`
                          : "There are no categories defined yet."}
                      </p>
                      {searchQuery && (
                        <Button
                          variant="outline"
                          onClick={() => setSearchQuery("")}
                          className="mt-2"
                          size="sm"
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedCategories.map((cat) => cat.id)}
                    strategy={verticalListSortingStrategy}
                    disabled={sortBy !== "position"}
                  >
                    {sortedCategories.map((category) => (
                      <SortableCategoryRow
                        key={category.id}
                        category={category}
                        onEdit={handleEditCategory}
                        onDelete={handleDeleteCategory}
                        onToggleActive={handleToggleActive}
                        renderCategoryIcon={renderCategoryIcon}
                        showDetails={showDetails}
                        sortBy={sortBy}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </AdminLayout>
  );
};

export default CategoriesList;
